import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Building2,
  Cake,
  CalendarDays,
  Camera,
  ChartNoAxesCombined,
  FileSignature,
  FileText,
  Gauge,
  Handshake,
  HeartHandshake,
  Hourglass,
  IdCard,
  Inbox,
  Milestone,
  Package,
  PlugZap,
  Repeat,
  Settings2,
  Sparkles,
  Stethoscope,
  Sunrise,
  Tags,
  Target,
  TrendingUp,
  Truck,
  Upload,
  UserRoundCog,
  Users,
  UsersRound,
  WalletCards,
} from "lucide-react";

export type NavigationAccess = {
  /** Role gate of the finance routes (`role:admin|accountant` on the API). */
  canViewFinancial: boolean;
  // SEC-GATES-02: the Financeiro children follow the user's real grants.
  canViewFinanceAccounts: boolean;
  canViewBudgets: boolean;
  canViewCategories: boolean;
  canViewInbox: boolean;
  canViewReports: boolean;
  canViewPatients: boolean;
  canViewSchedulings: boolean;
  canViewUsers: boolean;
  canViewPositions: boolean;
  canViewAgreements: boolean;
  canViewEmployees: boolean;
  canViewProcedures: boolean;
  canViewProviders: boolean;
  canViewModels: boolean;
  canViewModelTypes: boolean;
  canViewRoles: boolean;
  isSystemAdmin: boolean;
  isClinicAdmin: boolean;
};

export type NavigationItem = {
  title: string;
  icon: LucideIcon;
  url?: string;
  children?: NavigationItem[];
  accent?: "growth";
};

export type NavigationSection = {
  label: string;
  items: NavigationItem[];
};

function compact(items: Array<NavigationItem | false>): NavigationItem[] {
  return items.filter((item): item is NavigationItem => Boolean(item));
}

export function canSeeFinancialNavigation(roleNames: string[]): boolean {
  return roleNames.some((role) => ["admin", "accountant"].includes(role));
}

// SEC-GATES-02: the group needs the finance role and at least one child the
// user's grants open; each child follows its own list endpoint.
function financeItem(access: NavigationAccess): NavigationItem | false {
  if (!access.canViewFinancial) return false;
  const children = compact([
    access.canViewFinanceAccounts && {
      title: "Contas & fluxo de caixa",
      url: "/financial/account",
      icon: ChartNoAxesCombined,
    },
    access.canViewBudgets && {
      title: "Orçamentos",
      url: "/financial/budgets",
      icon: FileText,
    },
    access.canViewCategories && {
      title: "Categorias",
      url: "/financial/category",
      icon: Tags,
    },
  ]);
  return children.length > 0 && { title: "Financeiro", icon: WalletCards, children };
}

export function buildAppNavigation(
  access: NavigationAccess
): NavigationSection[] {
  const canManageAccess = access.canViewUsers || access.canViewRoles;

  const sections: NavigationSection[] = [
    {
      label: "Principal",
      items: compact([
        { title: "Hoje", url: "/", icon: Sparkles },
        access.canViewSchedulings && {
          title: "Agenda",
          url: "/schedulings",
          icon: CalendarDays,
        },
        // SEC-GATES-02: hidden from the accountant (role name; the Inbox
        // routes carry no permission yet).
        access.canViewInbox && { title: "Inbox", url: "/inbox", icon: Inbox },
        access.canViewPatients && {
          title: "Pacientes",
          url: "/patients",
          icon: Users,
        },
      ]),
    },
    {
      label: "Crescimento",
      items: [
        { title: "Leads", url: "/crm/leads", icon: UsersRound },
        {
          title: "Oportunidades",
          url: "/crm/deals",
          icon: TrendingUp,
        },
        // O-17 (SPEC-NAV-01 §2.3): the funnel stages catalog had a working
        // route and no way in.
        {
          title: "Etapas do funil",
          url: "/crm/stages",
          icon: Milestone,
        },
      ],
    },
    {
      label: "Clínica",
      items: compact([
        access.canViewEmployees && {
          title: "Profissionais",
          url: "/registrations/collaborators",
          icon: Stethoscope,
        },
        // O-17 (SPEC-NAV-01 §2.4): the cargos CRUD was orphaned; the flag
        // was computed for `canManageAccess` only.
        access.canViewPositions && {
          title: "Cargos",
          url: "/registrations/roles",
          icon: IdCard,
        },
        access.canViewProcedures && {
          title: "Procedimentos",
          url: "/registrations/procedures",
          icon: HeartHandshake,
        },
        // C-11: four working clinic screens had no menu entry (§4.3 of the
        // consolidation audit). Convênios follows the same `convenants`
        // permission the rest of the section uses. O-16 (SCRUM-270) retired
        // "Anamneses": the catalog wrote tables nothing read.
        {
          title: "Frequências",
          url: "/registrations/frequencies",
          icon: Repeat,
        },
        {
          title: "Galeria Antes & Depois",
          url: "/gallery",
          icon: Camera,
        },
        { title: "Estoque", url: "/inventory", icon: Package },
        {
          title: "Lista de espera & NPS",
          url: "/waitlist",
          icon: Hourglass,
        },
        access.canViewAgreements && {
          title: "Convênios",
          url: "/registrations/agreements",
          icon: Handshake,
        },
        // SEC-GATES-01: the supplier list answers 403 without `view providers`.
        access.canViewProviders && {
          title: "Fornecedores",
          url: "/registrations/providers",
          icon: Truck,
        },
        access.canViewModels && {
          title: "Modelos de contrato",
          url: "/contracts/models",
          icon: FileSignature,
        },
      ]),
    },
    {
      label: "Gestão",
      items: compact([
        financeItem(access),
        // SCRUM-134: reports are their own destination. They sat three levels
        // deep inside Financeiro, behind the financial gate, and the owner could
        // not find them; two report pages had no entry at all.
        access.canViewReports && {
          title: "Relatórios",
          icon: BarChart3,
          children: compact([
            {
              title: "Agenda",
              url: "/reports/schedulings",
              icon: CalendarDays,
            },
            {
              title: "Financeiro",
              url: "/reports/entries",
              icon: ChartNoAxesCombined,
            },
            {
              title: "Aniversariantes",
              url: "/reports/birthdays",
              icon: Cake,
            },
            // C-06: "Mensagens" was a table wired to a literal empty response.
            // Conversation volume is measured in Analytics de conversas.
            { title: "Analytics de conversas", url: "/analytics", icon: Target },
            // W2-10 (SCRUM-184): the daily briefing is `role:admin|accountant`
            // on the API, like attribution. Same flag, same reason.
            access.canViewFinancial && {
              title: "Briefing de hoje",
              url: "/relatorios/briefing",
              icon: Sunrise,
            },
            // SCRUM-99: the attribution report reads are `role:admin|accountant`
            // on the API (SCRUM-95). Without this flag an `employee` saw the
            // item and got a 403 after clicking it.
            access.canViewFinancial && {
              title: "Atribuição de agendamentos",
              url: "/relatorios/atribuicao",
              icon: TrendingUp,
            },
          ]),
        },
        {
          title: "Configurações",
          icon: Settings2,
          children: compact([
            // SCRUM-208 (audit C-14): the clinic record and the plan were
            // reachable from inside settings only. The sidebar group and the
            // settings sub-navigation now name the same destinations.
            //
            // O-14 (owner, 2026-09-17): the account is a settings destination
            // like the others, so "Minha conta" opens the group instead of
            // hiding in the avatar menu only. Channel transport remains here;
            // AI configuration belongs to the focused team workspace.
            {
              title: "Conta pessoal",
              url: "/settings/account",
              icon: UserRoundCog,
            },
            {
              title: "Clínica",
              url: "/settings/clinic",
              icon: Building2,
            },
            canManageAccess && {
              title: "Equipe e acessos",
              url: access.canViewUsers ? "/registrations/users" : "/permissions",
              icon: UserRoundCog,
            },
            {
              title: "Canais",
              url: "/settings/integrations",
              icon: PlugZap,
            },
            {
              title: "Plano e consumo",
              url: "/settings/usage",
              icon: Gauge,
            },
            // W3-03 (SCRUM-52): imports create clinic records in bulk and are
            // `role:admin` on the API. Gate the entry point the same way.
            access.isClinicAdmin && {
              title: "Importar dados",
              url: "/importacoes",
              icon: Upload,
            },
          ]),
        },
      ]),
    },
  ];

  return sections.filter((section) => section.items.length > 0);
}
